import type { CoverResponse, CreditInsurerRequestResponse } from "@asap/shared/schemas/credit-insurance";
import { useToast } from "~/composables/useToast";
import { useCreditInsurerRequest } from "~/components/company/composables/useCreditInsurerRequest";
import { useCreditInsurerCover } from "~/components/company/composables/useCreditInsurerCover";
import { useCoverageTracking } from "~/components/company/composables/useCoverageTracking";
import { getCoverageNoticeState } from "~/utils/credit-insurer-coverage";

const DEFAULT_PAGE_SIZE = 20;

// State, fetches and handlers of a company's credit guarantee tab: the history page and the covers.
export const useCompanyCreditGuarantee = (companyId: Ref<string>) => {
  const { t } = useI18n();
  const {
    selectAllByCompany: selectAllRequests,
    selectPageByCompany: selectRequestsPage,
    checkState,
  } = useCreditInsurerRequest();
  const { selectAllByCompany: selectCovers, syncByCompany: syncCoversByCompany } = useCreditInsurerCover();
  const { addToastError, addToastSuccess } = useToast();
  const coverageTracking = useCoverageTracking();

  const requests = ref<CreditInsurerRequestResponse[]>([]);
  const totalRequests = ref(0);
  const covers = ref<CoverResponse[]>([]);
  const isLoadingRequests = ref(true);
  const hasLoadedRequests = ref(false);
  const isLoadingCovers = ref(true);
  const isSyncingCovers = ref(false);
  const checkingId = ref<string | null>(null);

  const search = ref("");
  const pageSize = ref(DEFAULT_PAGE_SIZE);
  const pageIndex = ref(0);

  const currentCoveredAmount = computed(() =>
    covers.value
      .filter((cover) => cover.status === "approved")
      .reduce((sum, cover) => sum + (cover.totalAmount ?? 0), 0)
  );

  const lastCoverUpdate = computed(() =>
    covers.value
      .map((cover) => cover.lastCheckedAt ?? cover.updatedAt)
      .sort()
      .at(-1)
  );

  // Only the latest query may fill the table: a slow answer to an older search must not overwrite it.
  let latestRequestsQueryId = 0;

  const queryRequests = async () => {
    const queryId = ++latestRequestsQueryId;
    isLoadingRequests.value = true;
    try {
      const response = await selectRequestsPage(companyId.value, {
        search: search.value.trim() || undefined,
        pageIndex: pageIndex.value,
        pageSize: pageSize.value,
      });
      if (queryId !== latestRequestsQueryId) return;
      requests.value = response.items;
      totalRequests.value = response.totalItems;
      hasLoadedRequests.value = true;
    } catch (error) {
      if (queryId !== latestRequestsQueryId) return;
      addToastError({ title: t("companies.creditGuarantee.toast.loadRequestsError") }, error);
    } finally {
      if (queryId === latestRequestsQueryId) isLoadingRequests.value = false;
    }
  };

  const queryCovers = async () => {
    isLoadingCovers.value = true;
    try {
      covers.value = await selectCovers(companyId.value);
    } catch (error) {
      addToastError({ title: t("companies.creditGuarantee.toast.loadCoversError") }, error);
    } finally {
      isLoadingCovers.value = false;
    }
  };

  const refresh = () => Promise.all([queryRequests(), queryCovers()]);

  const syncCovers = async () => {
    isSyncingCovers.value = true;
    try {
      covers.value = await syncCoversByCompany(companyId.value);
      addToastSuccess({ title: t("companies.creditGuarantee.toast.synced") });
    } catch (error) {
      addToastError({ title: t("companies.creditGuarantee.toast.syncError") }, error);
    } finally {
      isSyncingCovers.value = false;
    }
  };

  const runCheck = async (id: string) => {
    checkingId.value = id;
    try {
      await checkState(id);
      // A resolved request may have created/updated a cover.
      await refresh();
      addToastSuccess({ title: t("companies.creditGuarantee.toast.checked") });
    } catch (error) {
      addToastError({ title: t("companies.creditGuarantee.toast.checkError") }, error);
    } finally {
      checkingId.value = null;
    }
  };

  // The coverage state needs the whole history, not just the page on screen.
  const trackTabViewed = async () => {
    let coverageState: ReturnType<typeof getCoverageNoticeState>["kind"] | "error";
    try {
      const [allRequests, allCovers] = await Promise.all([
        selectAllRequests(companyId.value),
        selectCovers(companyId.value),
      ]);
      coverageState = getCoverageNoticeState(allCovers, allRequests).kind;
    } catch {
      coverageState = "error";
    }
    coverageTracking.trackTabViewed(companyId.value, coverageState);
  };

  watch([search, pageSize], () => {
    pageIndex.value = 0;
  });
  watch([search, pageSize, pageIndex], queryRequests);

  onMounted(() => {
    refresh();
    trackTabViewed();
  });

  return {
    requests,
    totalRequests,
    isLoadingRequests,
    hasLoadedRequests,
    isLoadingCovers,
    isSyncingCovers,
    checkingId,
    search,
    pageSize,
    pageIndex,
    currentCoveredAmount,
    lastCoverUpdate,
    refresh,
    syncCovers,
    runCheck,
  };
};
